import { useState, useRef } from "react"
import { Head, Link, router } from "@inertiajs/react"
import { AdminShell } from "@/components/AdminShell"
import { Button } from "@/components/ui/button"
import { ScanText, ChevronDown, ChevronUp, Loader2 } from "lucide-react"

type Attachment = {
  id: number
  attachable_type: string
  attachable_id: number
  attachable_name: string | null
  is_primary: boolean
  position: number
}

type ImageDetail = {
  id: number
  caption: string | null
  alt_text: string | null
  credit: string | null
  credit_url: string | null
  attachment_count: number
  tag_names: string[]
  has_file: boolean
  url: string | null
  attachments: Attachment[]
  created_at: string
  updated_at: string
}

type SearchResult = { id: number; name: string }
const ATTACHABLE_TYPES = ["Band", "Person", "Recording", "Venue", "Gig"]

type MatchInfo = {
  status: "confident_match" | "possible_match" | "no_match"
  id: number | null
  name: string | null
  type?: string
  city?: string | null
  state?: string | null
}

// Venues frequently share a name across different cities/states (e.g. two
// "Bombay Rock" venues, one in Brunswick VIC and one in Surfers Paradise
// QLD) — without the location, a fuzzy name match can silently resolve to
// the wrong one. See #299.
function matchLabel(match: { name: string | null; city?: string | null; state?: string | null }): string {
  if (!match.name) return ""
  return match.city && match.state ? `${match.name} — ${match.city}, ${match.state}` : match.name
}

type ExistingGig = {
  id: number
  gig_date: string | null
  notes: string | null
}

type GigProposal = {
  id: string
  artist_name: string
  venue_name: string
  date_text: string
  gig_date: string | null
  year_known: boolean
  day: number | null
  month: number | null
  notes: string | null
  artist_match: MatchInfo
  venue_match: MatchInfo
  existing_gig: ExistingGig | null
  possible_years: number[] | null
}

// Resolution choices made by the user for a single proposal
type Resolution = {
  accepted: boolean
  artist_id: number | null
  artist_type: string
  create_artist: boolean
  venue_id: number | null
  venue_name_resolved: string | null   // the name of the venue the user actually selected
  create_venue: boolean
  venue_city: string
  venue_state: string
  selected_year: number | null
}

function EntitySearch({
  onSelect,
  fixedType,
}: {
  onSelect: (id: number, name: string, type: string) => void
  fixedType?: string
}) {
  const [type, setType] = useState(fixedType ?? "Band")
  const [query, setQuery] = useState("")
  const [results, setResults] = useState<SearchResult[]>([])
  const [selectedName, setSelectedName] = useState<string | null>(null)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)

  function handleTypeChange(newType: string) {
    setType(newType)
    setQuery("")
    setResults([])
    setSelectedName(null)
  }

  function handleQueryChange(value: string) {
    setQuery(value)
    setSelectedName(null)
    if (timer.current) clearTimeout(timer.current)
    if (value.length < 2) { setResults([]); return }
    timer.current = setTimeout(async () => {
      const res = await fetch(`/admin/entities/search?type=${type}&q=${encodeURIComponent(value)}`)
      setResults(await res.json())
    }, 250)
  }

  function handleSelect(result: SearchResult) {
    setSelectedName(result.name)
    setQuery(result.name)
    setResults([])
    onSelect(result.id, result.name, type)
  }

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        {!fixedType && (
          <select
            value={type}
            onChange={(e) => handleTypeChange(e.target.value)}
            className="rounded border border-hairline bg-page px-2 py-1 text-sm text-ink-body focus:outline-none focus:ring-1 focus:ring-current"
          >
            {ATTACHABLE_TYPES.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        )}
        <div className="relative flex-1">
          <input
            type="text"
            value={query}
            onChange={(e) => handleQueryChange(e.target.value)}
            placeholder={`Search ${type} by name…`}
            className="w-full rounded border border-hairline bg-page px-2 py-1 text-sm text-ink-body focus:outline-none focus:ring-1 focus:ring-current"
          />
          {results.length > 0 && (
            <ul className="absolute z-10 left-0 right-0 top-full mt-1 rounded border border-hairline bg-page shadow-sm divide-y divide-hairline">
              {results.map((r) => (
                <li
                  key={r.id}
                  onClick={() => handleSelect(r)}
                  className="px-3 py-2 text-sm text-ink-body hover:bg-surface cursor-pointer"
                >
                  {r.name}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      {selectedName && (
        <p className="text-xs text-ink-muted">
          Selected: <span className="font-medium text-ink-body">{selectedName}</span>
        </p>
      )}
      {!selectedName && query.length >= 2 && results.length === 0 && (
        <p className="text-xs text-ink-muted italic">No results — try a different name</p>
      )}
    </div>
  )
}

function MatchBadge({ match }: { match: MatchInfo }) {
  if (match.status === "confident_match") {
    return (
      <span className="inline-flex items-center gap-1 rounded bg-green-50 px-2 py-0.5 text-xs text-green-700 dark:bg-green-900/30 dark:text-green-400">
        ✓ {matchLabel(match)} ({match.type ?? "Venue"})
      </span>
    )
  }
  if (match.status === "possible_match") {
    return (
      <span className="inline-flex items-center gap-1 rounded bg-amber-50 px-2 py-0.5 text-xs text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
        ? Possible: {matchLabel(match)} ({match.type ?? "Venue"})
      </span>
    )
  }
  return (
    <span className="inline-flex items-center gap-1 rounded bg-surface px-2 py-0.5 text-xs text-ink-muted">
      No match found
    </span>
  )
}

function GigProposalRow({
  proposal,
  resolution,
  onChange,
}: {
  proposal: GigProposal
  resolution: Resolution
  onChange: (r: Partial<Resolution>) => void
}) {
  function handleArtistSelect(id: number, _name: string, type: string) {
    onChange({ artist_id: id, artist_type: type, create_artist: false })
  }


  return (
    <div className={`rounded-md border p-4 space-y-3 ${resolution.accepted ? "border-hairline" : "border-hairline opacity-50"}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2">
          <input
            type="checkbox"
            checked={resolution.accepted}
            onChange={(e) => onChange({ accepted: e.target.checked })}
            className="mt-0.5 h-4 w-4 shrink-0"
          />
          <div className="space-y-0.5">
            <p className="text-sm font-medium text-ink-display">{proposal.artist_name}</p>
            <p className="text-xs text-ink-muted">
              {resolution.venue_name_resolved
                ? <span className="text-green-700 dark:text-green-400">{resolution.venue_name_resolved}</span>
                : proposal.venue_name
              }{" "}&mdash; {proposal.date_text}
            </p>
            {proposal.notes && <p className="text-xs text-ink-muted italic">{proposal.notes}</p>}
            {proposal.existing_gig && (
              <p className="mt-1 inline-flex items-center gap-1 rounded bg-orange-50 px-2 py-0.5 text-xs text-orange-700 dark:bg-orange-900/30 dark:text-orange-400">
                Gig #{proposal.existing_gig.id} already exists for this date — leave checked to attach this image to it
              </p>
            )}
          </div>
        </div>
      </div>

      {proposal.possible_years && proposal.possible_years.length > 0 && (
        <div className="pl-6">
          <p className="text-xs font-medium text-ink-muted mb-1.5">
            Year unknown — select from possible years where {proposal.date_text} matches:
          </p>
          <div className="flex flex-wrap gap-1.5">
            {proposal.possible_years.map((year) => (
              <button
                key={year}
                onClick={() => onChange({
                  selected_year: resolution.selected_year === year ? null : year
                })}
                className={`rounded px-2.5 py-1 text-xs font-medium transition-colors ${
                  resolution.selected_year === year
                    ? "bg-blue-600 text-white"
                    : "bg-surface text-ink-body hover:bg-blue-50 hover:text-blue-700 border border-hairline dark:hover:bg-blue-900/30 dark:hover:text-blue-300"
                }`}
              >
                {year}
              </button>
            ))}
          </div>
          {resolution.selected_year && (
            <p className="mt-1.5 text-xs text-green-700 dark:text-green-400">
              ✓ {proposal.date_text} {resolution.selected_year} selected
            </p>
          )}
        </div>
      )}

      {resolution.accepted && (
        <div className="pl-6 space-y-3">
          {/* Artist resolution */}
          <div>
            <p className="text-xs font-medium text-ink-muted mb-1">Artist</p>
            <MatchBadge match={proposal.artist_match} />
            {proposal.artist_match.status !== "no_match" && !resolution.create_artist && resolution.artist_id === null && (
              <button
                onClick={() => onChange({
                  artist_id: proposal.artist_match.id,
                  artist_type: proposal.artist_match.type ?? "Band",
                  create_artist: false
                })}
                className="ml-2 text-xs text-blue-600 hover:underline"
              >
                Accept match
              </button>
            )}
            {resolution.artist_id !== null && !resolution.create_artist && (
              <span className="ml-2 text-xs text-green-700">✓ Selected</span>
            )}
            <div className="mt-2 space-y-1">
              <p className="text-xs text-ink-muted">Override with search:</p>
              <EntitySearch onSelect={handleArtistSelect} />
            </div>
            <label className="mt-2 flex items-center gap-2 text-xs text-ink-body">
              <input
                type="checkbox"
                checked={resolution.create_artist}
                onChange={(e) => onChange({ create_artist: e.target.checked, artist_id: e.target.checked ? null : resolution.artist_id })}
                className="h-3.5 w-3.5"
              />
              Create new record for &ldquo;{proposal.artist_name}&rdquo;
            </label>
          </div>

          {/* Venue resolution */}
          <div>
            <p className="text-xs font-medium text-ink-muted mb-1">Venue</p>
            <MatchBadge match={{ ...proposal.venue_match, type: undefined }} />
            {proposal.venue_match.status !== "no_match" && !resolution.create_venue && resolution.venue_id === null && (
              <button
                onClick={() => onChange({ venue_id: proposal.venue_match.id, create_venue: false, venue_name_resolved: matchLabel(proposal.venue_match) })}
                className="ml-2 text-xs text-blue-600 hover:underline"
              >
                Accept match
              </button>
            )}
            {resolution.venue_id !== null && !resolution.create_venue && (
              <span className="ml-2 text-xs text-green-700">✓ Selected</span>
            )}
            <div className="mt-2 space-y-1">
              <p className="text-xs text-ink-muted">Override with search:</p>
              <EntitySearch
                fixedType="Venue"
                onSelect={(id, name, _type) => onChange({ venue_id: id, create_venue: false, venue_name_resolved: name })}
              />
            </div>
            <label className="mt-2 flex items-center gap-2 text-xs text-ink-body">
              <input
                type="checkbox"
                checked={resolution.create_venue}
                onChange={(e) => onChange({ create_venue: e.target.checked, venue_id: e.target.checked ? null : resolution.venue_id })}
                className="h-3.5 w-3.5"
              />
              Create new venue &ldquo;{proposal.venue_name}&rdquo;
            </label>
            {resolution.create_venue && (
              <div className="mt-2 flex gap-2">
                <input
                  type="text"
                  placeholder="City"
                  value={resolution.venue_city}
                  onChange={(e) => onChange({ venue_city: e.target.value })}
                  className="flex-1 rounded border border-hairline bg-page px-2 py-1 text-sm text-ink-body focus:outline-none"
                />
                <input
                  type="text"
                  placeholder="State (e.g. NSW)"
                  value={resolution.venue_state}
                  onChange={(e) => onChange({ venue_state: e.target.value })}
                  className="w-32 rounded border border-hairline bg-page px-2 py-1 text-sm text-ink-body focus:outline-none"
                />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

function defaultResolution(proposal: GigProposal): Resolution {
  const artistId = proposal.artist_match.status === "confident_match" ? proposal.artist_match.id : null
  const artistType = proposal.artist_match.type ?? "Band"
  const venueId = proposal.venue_match.status === "confident_match" ? proposal.venue_match.id : null
  const venueNameResolved = proposal.venue_match.status === "confident_match" ? matchLabel(proposal.venue_match) : null
  return {
    // Matched proposals stay checked: confirming attaches the image to the
    // existing gig rather than creating a duplicate (#375, #376).
    accepted: true,
    artist_id: artistId,
    artist_type: artistType,
    create_artist: false,
    venue_id: venueId,
    venue_name_resolved: venueNameResolved,
    create_venue: false,
    venue_city: "",
    venue_state: "",
    selected_year: null,
  }
}

type DuplicateCheck = {
  proposal_id: string
  artist_id: number | null
  artist_type: string
  venue_id: number | null
  gig_date: string | null
  day: number | null
  month: number | null
  selected_year: number | null
}

function GigExtractionPanel({
  imageId, hasFile, caption, attachments,
}: {
  imageId: number
  hasFile: boolean
  caption?: string | null
  attachments?: Attachment[]
}) {
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [context, setContext] = useState("")
  const [proposals, setProposals] = useState<GigProposal[] | null>(null)
  const [resolutions, setResolutions] = useState<Record<string, Resolution>>({})
  const [error, setError] = useState<string | null>(null)
  const [confirming, setConfirming] = useState(false)
  const confirmGuard = useRef(false)   // synchronous guard — prevents double-submit before React re-render

  async function handleExtract() {
    setLoading(true)
    setError(null)
    setProposals(null)
    try {
      const csrf = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? ""
      const body = new FormData()
      if (context.trim()) body.append("context", context.trim())
      const res = await fetch(`/admin/images/${imageId}/extract_gig_data`, {
        method: "POST",
        headers: { "X-CSRF-Token": csrf, "Accept": "application/json" },
        body,
      })
      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        throw new Error((body as { error?: string }).error ?? `Server error (${res.status})`)
      }
      const data = await res.json() as { proposals: GigProposal[] }
      setProposals(data.proposals)
      const initial: Record<string, Resolution> = {}
      data.proposals.forEach((p) => { initial[p.id] = defaultResolution(p) })
      setResolutions(initial)
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not extract gig data. Please try again.")
    } finally {
      setLoading(false)
    }
  }

  async function handleResolutionChange(proposalId: string, changes: Partial<Resolution>) {
    const prevResolution = resolutions[proposalId]
    const venueChanged = "venue_id" in changes && changes.venue_id !== prevResolution?.venue_id
    // The lookup is (artist, venue, date) — so any of them changing invalidates
    // a previous duplicate check. Only venue used to trigger a re-check, which
    // is how artist and year overrides slipped duplicates through (#375).
    const lookupChanged =
      venueChanged ||
      ("artist_id" in changes && changes.artist_id !== prevResolution?.artist_id) ||
      ("artist_type" in changes && changes.artist_type !== prevResolution?.artist_type) ||
      ("selected_year" in changes && changes.selected_year !== prevResolution?.selected_year)

    // Start with the directly-changed proposal
    let newResolutions: Record<string, Resolution> = {
      ...resolutions,
      [proposalId]: { ...prevResolution, ...changes },
    }

    // When venue changes, propagate to all proposals sharing the same extracted venue_name
    const affectedIds: string[] = [proposalId]
    if (venueChanged && proposals) {
      const changedProposal = proposals.find((p) => p.id === proposalId)
      if (changedProposal) {
        proposals.forEach((p) => {
          if (p.id !== proposalId && p.venue_name === changedProposal.venue_name) {
            newResolutions = {
              ...newResolutions,
              [p.id]: {
                ...newResolutions[p.id],
                venue_id: changes.venue_id ?? null,
                venue_name_resolved: changes.venue_name_resolved ?? null,
                create_venue: false,
              },
            }
            affectedIds.push(p.id)
          }
        })
      }
    }

    setResolutions(newResolutions)

    // Re-check for duplicate gigs whenever the lookup inputs change.
    if (lookupChanged && proposals) {
      // Send the same shape confirm_gig_data sends, so the server resolves the
      // date identically — including one built from an overridden year.
      const checks: DuplicateCheck[] = affectedIds.flatMap((pid) => {
        const p = proposals.find((pr) => pr.id === pid)
        const r = newResolutions[pid]
        if (!p || !r) return []
        return [{
          proposal_id: pid,
          artist_id: r.artist_id,
          artist_type: r.artist_type,
          venue_id: r.venue_id,
          gig_date: p.gig_date,
          day: p.day,
          month: p.month,
          selected_year: r.selected_year,
        }]
      })

      if (checks.length > 0) {
        try {
          const csrf = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? ""
          const res = await fetch(`/admin/images/${imageId}/check_gig_duplicate`, {
            method: "POST",
            headers: { "X-CSRF-Token": csrf, "Accept": "application/json", "Content-Type": "application/x-www-form-urlencoded" },
            body: `checks=${encodeURIComponent(JSON.stringify(checks))}`,
          })
          if (res.ok) {
            const results = (await res.json()) as Record<string, { id: number; gig_date: string } | null>
            // Update existing_gig on each affected proposal
            setProposals((prev) =>
              prev === null ? null : prev.map((p) => {
                // A proposal we checked but got no answer for is undetermined,
                // not "still a duplicate" — clear any stale match. The server
                // guard is what actually prevents duplicates either way.
                if (!affectedIds.includes(p.id)) return p
                const hit = results[p.id]
                return { ...p, existing_gig: hit ? { id: hit.id, gig_date: hit.gig_date, notes: null } : null }
              })
            )
            // Deliberately no auto-unchecking: a matched proposal stays checked
            // so confirming attaches the image to the gig that already exists.
          }
        } catch {
          // Duplicate check is non-critical; ignore failures silently
        }
      }
    }
  }

  function handleConfirm() {
    if (!proposals || confirmGuard.current) return
    confirmGuard.current = true   // block any re-click before React re-renders
    setConfirming(true)
    const accepted = proposals
      .filter((p) => resolutions[p.id]?.accepted)
      .map((p) => {
        const r = resolutions[p.id]
        return {
          id: p.id,
          artist_name: p.artist_name,
          artist_id: r.artist_id,
          artist_type: r.artist_type,
          create_artist: r.create_artist,
          venue_name: p.venue_name,
          venue_id: r.venue_id,
          create_venue: r.create_venue,
          venue_city: r.venue_city,
          venue_state: r.venue_state,
          gig_date: p.gig_date,
          date_text: p.date_text,
          day: p.day,
          month: p.month,
          selected_year: r.selected_year,
          notes: p.notes,
        }
      })
    router.post(`/admin/images/${imageId}/confirm_gig_data`, { proposals: JSON.stringify(accepted) }, {
      onFinish: () => { setConfirming(false); confirmGuard.current = false },
    })
  }

  const acceptedCount = proposals
    ? proposals.filter((p) => resolutions[p.id]?.accepted).length
    : 0

  return (
    <div className="rounded-md border border-hairline">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between px-4 py-3 text-left"
      >
        <div className="flex items-center gap-2">
          <ScanText className="h-4 w-4 text-blue-600 dark:text-blue-400" />
          <span className="text-sm font-medium text-ink-display">Extract gig data from this image</span>
        </div>
        {open ? <ChevronUp className="h-4 w-4 text-ink-muted" /> : <ChevronDown className="h-4 w-4 text-ink-muted" />}
      </button>

      {open && (
        <div className="border-t border-hairline px-4 pb-4 pt-3 space-y-4">
          <p className="text-xs text-ink-muted">
            Send this image to the AI to extract gig information (bands, venues, dates). Results depend on image quality — review all proposals carefully before confirming.
          </p>

          {!hasFile && (
            <p className="text-xs text-amber-600">No image file attached — upload a file first.</p>
          )}

          {hasFile && !proposals && (
            <div className="space-y-3">
              {(caption || (attachments && attachments.length > 0)) && (
                <div className="rounded bg-surface px-3 py-2 text-xs text-ink-muted space-y-0.5">
                  <p className="font-medium text-ink-body">Sent automatically as context:</p>
                  {caption && (
                    <p>Caption: <span className="italic">{caption}</span></p>
                  )}
                  {attachments && attachments.length > 0 && (
                    <p>
                      Attachments:{" "}
                      {attachments
                        .map((a) => `${a.attachable_name ?? `#${a.attachable_id}`} (${a.attachable_type})`)
                        .join(", ")}
                    </p>
                  )}
                </div>
              )}
              <div>
                <label className="block text-xs font-medium text-ink-muted mb-1">
                  Additional context <span className="font-normal">(optional)</span>
                </label>
                <textarea
                  value={context}
                  onChange={(e) => setContext(e.target.value)}
                  placeholder="e.g. These shows are from March 1979 — the year isn't printed on the poster"
                  rows={2}
                  className="w-full rounded border border-hairline bg-page px-2 py-1.5 text-sm text-ink-body placeholder:text-ink-muted focus:outline-none focus:ring-1 focus:ring-current resize-none"
                />
              </div>
              <Button size="sm" onClick={handleExtract} disabled={loading} className="flex items-center gap-1.5">
                {loading && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                {loading ? "Extracting…" : "Extract gig data"}
              </Button>
            </div>
          )}

          {error && (
            <p className="text-sm text-red-600">{error}</p>
          )}

          {proposals !== null && (
            <div className="space-y-3">
              {proposals.length === 0 ? (
                <p className="text-sm text-ink-muted italic">No gig information found in this image.</p>
              ) : (
                <>
                  <p className="text-xs text-ink-muted">
                    Found {proposals.length} gig{proposals.length !== 1 ? "s" : ""}. Review each proposal, resolve any ambiguous matches, then confirm.
                  </p>
                  <div className="space-y-2">
                    {proposals.map((p) => (
                      <GigProposalRow
                        key={p.id}
                        proposal={p}
                        resolution={resolutions[p.id] ?? defaultResolution(p)}
                        onChange={(changes) => handleResolutionChange(p.id, changes)}
                      />
                    ))}
                  </div>
                  <div className="flex items-center gap-3">
                    <Button
                      size="sm"
                      onClick={handleConfirm}
                      disabled={confirming || acceptedCount === 0}
                      className="flex items-center gap-1.5"
                    >
                      {confirming && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                      {confirming ? "Saving…" : `Confirm ${acceptedCount} gig${acceptedCount !== 1 ? "s" : ""}`}
                    </Button>
                    {!confirming && (
                      <button
                        onClick={() => { setProposals(null); setResolutions({}); setContext(""); confirmGuard.current = false }}
                        className="text-xs text-ink-muted hover:text-ink-body"
                      >
                        Start over
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default function AdminImageShow({ image }: { image: ImageDetail }) {
  const [attachForm, setAttachForm] = useState<{
    attachable_id: number | ""
    attachable_type: string
    is_primary: boolean
  }>({ attachable_id: "", attachable_type: "Band", is_primary: false })

  function handleEntitySelect(id: number, _name: string, type: string) {
    setAttachForm((prev) => ({ ...prev, attachable_id: id, attachable_type: type }))
  }

  function handleAttachSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!attachForm.attachable_id) return
    router.post(`/admin/images/${image.id}/attach`, {
      attachment: {
        attachable_type: attachForm.attachable_type,
        attachable_id: attachForm.attachable_id,
        is_primary: attachForm.is_primary,
      }
    })
    setAttachForm({ attachable_id: "", attachable_type: "Band", is_primary: false })
  }

  function handleRemoveAttachment(attachmentId: number) {
    if (confirm("Remove this attachment?")) {
      router.delete(`/admin/image_attachments/${attachmentId}`)
    }
  }

  function handleDelete() {
    if (confirm("Delete this image? This cannot be undone.")) {
      router.delete(`/admin/images/${image.id}`)
    }
  }

  const title = image.caption || `Image #${image.id}`

  return (
    <>
      <Head title={title}>
        <meta name="description" content={`Admin detail for image #${image.id}.`} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={`Admin detail for image #${image.id}.`} />
      </Head>
      <AdminShell>
        <div className="border-b border-hairline pb-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h1>{title}</h1>
              <p className="mt-1">Image #{image.id}</p>
            </div>
            <div className="flex items-center gap-2">
              <Button asChild variant="secondary">
                <Link href={`/admin/images/${image.id}/edit`}>Edit</Link>
              </Button>
              <Button variant="danger" onClick={handleDelete}>Delete</Button>
              <Link href="/admin/images" className="text-sm text-ink-muted no-underline hover:text-ink-display">
                Back to images
              </Link>
            </div>
          </div>
        </div>

        {image.url ? (
          <div className="mt-6">
            <img
              src={image.url}
              alt={image.alt_text ?? ""}
              className="max-h-80 rounded-lg object-contain bg-surface"
            />
          </div>
        ) : (
          <div className="mt-6 flex h-40 items-center justify-center rounded-lg border border-hairline bg-surface">
            <p className="text-sm text-ink-muted italic">{image.has_file ? "Image URL unavailable" : "No file uploaded"}</p>
          </div>
        )}

        <div className="mt-6 space-y-8">
          <GigExtractionPanel
            imageId={image.id}
            hasFile={image.has_file}
            caption={image.caption}
            attachments={image.attachments}
          />

          <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
            <section>
              <h2 className="mb-3 text-sm font-medium text-ink-muted uppercase tracking-wide">Details</h2>
              <dl className="space-y-2 text-sm">
                <div className="flex gap-4">
                  <dt className="w-28 shrink-0 text-ink-muted">Caption</dt>
                  <dd className="text-ink-body">{image.caption || <span className="italic text-ink-muted">None</span>}</dd>
                </div>
                <div className="flex gap-4">
                  <dt className="w-28 shrink-0 text-ink-muted">Alt text</dt>
                  <dd className="text-ink-body">{image.alt_text || <span className="italic text-ink-muted">None</span>}</dd>
                </div>
                <div className="flex gap-4">
                  <dt className="w-28 shrink-0 text-ink-muted">Credit</dt>
                  <dd className="text-ink-body">
                    {image.credit
                      ? (image.credit_url
                          ? <a href={image.credit_url} target="_blank" rel="noopener noreferrer nofollow" className="text-amber-600 hover:underline dark:text-amber-400">{image.credit}</a>
                          : image.credit)
                      : <span className="italic text-ink-muted">None</span>}
                  </dd>
                </div>
                <div className="flex gap-4">
                  <dt className="w-28 shrink-0 text-ink-muted">File</dt>
                  <dd className="text-ink-body">{image.has_file ? "Attached" : <span className="italic text-ink-muted">No file</span>}</dd>
                </div>
                <div className="flex gap-4">
                  <dt className="w-28 shrink-0 text-ink-muted">Tags</dt>
                  <dd className="text-ink-body">
                    {image.tag_names.length > 0
                      ? image.tag_names.join(", ")
                      : <span className="italic text-ink-muted">None</span>}
                  </dd>
                </div>
                <div className="flex gap-4">
                  <dt className="w-28 shrink-0 text-ink-muted">Created</dt>
                  <dd className="text-ink-body">{new Date(image.created_at).toLocaleDateString()}</dd>
                </div>
              </dl>
            </section>

            <section>
              <h2 className="mb-3 text-sm font-medium text-ink-muted uppercase tracking-wide">
                Attachments ({image.attachment_count})
              </h2>
              {image.attachments.length === 0 ? (
                <p className="text-sm text-ink-muted italic">No attachments yet.</p>
              ) : (
                <ul className="divide-y divide-hairline rounded-md border border-hairline">
                  {image.attachments.map((attachment) => (
                    <li key={attachment.id} className="flex items-center justify-between px-3 py-2 text-sm">
                      <div>
                        <span className="font-medium text-ink-display">{attachment.attachable_type}</span>
                        <span className="text-ink-muted"> — {attachment.attachable_name || `#${attachment.attachable_id}`}</span>
                        {attachment.is_primary && (
                          <span className="ml-2 rounded bg-surface px-1.5 py-0.5 text-xs text-ink-muted">primary</span>
                        )}
                      </div>
                      <button
                        onClick={() => handleRemoveAttachment(attachment.id)}
                        className="text-xs text-red-600 hover:text-red-800"
                      >
                        Remove
                      </button>
                    </li>
                  ))}
                </ul>
              )}

              <form onSubmit={handleAttachSubmit} className="mt-4 space-y-3 rounded-md border border-hairline p-4">
                <h3 className="text-sm font-medium text-ink-display">Attach to entity</h3>
                <EntitySearch onSelect={handleEntitySelect} />
                <div className="flex items-center gap-2">
                  <input
                    id="is_primary"
                    type="checkbox"
                    checked={attachForm.is_primary}
                    onChange={(e) => setAttachForm((prev) => ({ ...prev, is_primary: e.target.checked }))}
                    className="h-4 w-4"
                  />
                  <label htmlFor="is_primary" className="text-sm text-ink-body">Set as primary image</label>
                </div>
                <Button type="submit" size="sm" disabled={!attachForm.attachable_id}>Attach</Button>
              </form>
            </section>
          </div>
        </div>
      </AdminShell>
    </>
  )
}
